/**
 * MoneyTransfersPage — `/financial-services/transfers`.
 *
 * Slice 9: wired to `GET /v1/financial-services/transfers` — platform-wide
 * P2P transfer ledger (all P2P_SEND transactions, paginated).
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  formatNgn,
  listTransfersLedger,
} from '../../lib/api/financial-services';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

export function MoneyTransfersPage(): JSX.Element {
  const [search, setSearch] = useState('');

  const transfers = useQuery({
    queryKey: ['financial-services', 'transfers-ledger'],
    queryFn: () => listTransfersLedger({ pageSize: 50 }),
    retry: false,
  });

  const denied =
    transfers.isError && transfers.error instanceof ApiError &&
    (transfers.error.status === 401 || transfers.error.status === 403);

  const filtered = (transfers.data?.items ?? []).filter((t) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return (
      t.txnNumber.toLowerCase().includes(needle) ||
      t.walletId.toLowerCase().includes(needle) ||
      (t.counterRef?.toLowerCase().includes(needle) ?? false)
    );
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Money transfers"
        description="Platform-wide P2P transfer ledger (all P2P_SEND transactions)."
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by txn number, wallet id, or counter-ref…"
      />

      {transfers.isPending ? (
        <p className="text-sm text-muted">Loading transfers…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Financials/readTransfers</code> required.
        </DeferredNotice>
      ) : transfers.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {(transfers.error as Error).message}
        </DeferredNotice>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No transfers found.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-nipost-green" />
              <h3 className="text-sm font-semibold text-gray-900">Transfer ledger</h3>
            </div>
            <span className="text-xs text-muted">{transfers.data?.total ?? 0} total P2P sends</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-[10px] uppercase tracking-wider text-muted border-b border-border">
              <tr>
                <th className="text-left px-4 py-2">Txn number</th>
                <th className="text-left px-4 py-2">Wallet</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Initiated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{t.txnNumber}</td>
                  <td className="px-4 py-2 font-mono text-[10px] text-muted">{t.walletId}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {formatNgn(t.amount)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(t.initiatedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
